import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ContentChange, QuillEditorComponent } from 'ngx-quill';
import { DialogService } from 'primeng/dynamicdialog';
import Quill from 'quill';
import { map } from 'rxjs/operators';

import { SubSink } from 'subsink';

import { QuillTemplate, QuillTemplateLocation } from '../../generated/graphql.generated';

import { FreyaCommonModule } from '../freya-common/freya-common.module';
import { commentDefaultToolbar } from '../shared/comments.utils';
import { TEMPLATE_VARIABLES } from '../shared/quill-modules/quill-templates.helpers';
import { VariableToolbarQuillModule } from '../shared/quill-modules/variableToolbar.quillModule';
import { SharedModule } from '../shared/shared.module';

import { brandingFeature } from '../state/branding.store';

import { TemplatesActions , TEMPLATES_WITH_SUBJECT, templatesFeature } from './state/templates.store';


@Component({
    selector: 'app-templates',
    standalone: true,
    imports: [FreyaCommonModule, SharedModule, QuillEditorComponent],
    providers: [DialogService],
    templateUrl: './templates.component.html',
    styleUrls: ['./templates.component.scss']
})
export class TemplatesComponent implements OnInit, OnDestroy {

    templatesWithSubjectContents = TEMPLATES_WITH_SUBJECT;

    templates$ = this.store.select(brandingFeature.selectTemplates);
    groupedTemplates$ = this.templates$.pipe(
        map(templates => {
            if (!templates) return {};
            return templates.reduce((acc, template) => {
                const location = template.location
                    ? template.location.replace(/_/g, ' ').toLowerCase()
                    : 'Unspecified';

                if (!acc[location]) acc[location] = [];
                acc[location].push({
                    ...template,
                    contents: JSON.parse(template.contents || '{}'),
                    subjectContents: template.subjectContents
                        ? JSON.parse(template.subjectContents)
                        : null
                });
                return acc;
            }, {} as Record<string, QuillTemplate[]>);
        })
    );

    quillEditorRefs: Record<string, Quill> = {};
    subjectQuillEditorRefs: Record<string, Quill> = {};

    totalRecords = 0;
    loading = false;
    modules = {
        toolbar: commentDefaultToolbar,
        variableToolbar: true,
    };

    templateContentEdits$ = this.store.select(templatesFeature.selectTemplateContentEdits);

    private subs = new SubSink();
    private dialogOpened = false; // Flag to track if the dialog is already opened

    constructor(
        private store: Store,
        private route: ActivatedRoute // Added ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.subs.sink = this.templates$.subscribe(templates => {
            this.totalRecords = templates?.length || 0;
        });

        // Listen to query params and check for 'location'
        this.subs.sink = this.route.queryParams.subscribe(params => {
            const location = params['location'];
            const id = params['id'];

            if (this.dialogOpened || (!location && !id)) return; // Prevent opening the dialog multiple times

            if (id) {
                // Open edit template dialog
                this.store.dispatch(TemplatesActions.editTemplateButtonClicked({ template: null, id }));
                this.dialogOpened = true;
            } else if (location) {
                // Open create template dialog with pre-filled location. The param
                // is whatever was typed into the URL, so anything that is not a
                // real context opens the dialog with the picker left empty rather
                // than seeding a value the dropdown cannot show and the backend
                // will refuse.
                this.addTemplate(this.asTemplateLocation(location));
                this.dialogOpened = true;
            }
        });
    }

    addTemplate(location?: QuillTemplateLocation): void {
        this.store.dispatch(TemplatesActions.createTemplateButtonClicked({location}));
    }

    editTemplate(template: QuillTemplate): void {
        this.store.dispatch(TemplatesActions.editTemplateButtonClicked({ template }));
    }

    deleteTemplate(template: QuillTemplate): void {
        this.store.dispatch(TemplatesActions.templateDeletionRequested({ id: template.id }));
    }

    setDefaultTemplate(template: QuillTemplate, _templates: QuillTemplate[]): void {
        this.loading = true;
        this.store.dispatch(TemplatesActions.templateUpdateRequested({
            input: {
                ids: [template.id],
                update: {
                    isDefault: !template.isDefault,
                    location: template.location,
                }
            }
        }));
        this.loading = false;
    }

    onEditorCreated(quillEditor: Quill, template: QuillTemplate, isSubject?: boolean): void {
        const templateVariables = TEMPLATE_VARIABLES[template.location];
        const variableToolbar = quillEditor?.getModule('variableToolbar') as VariableToolbarQuillModule;
        variableToolbar?.updateVariables(templateVariables);

        //Update the references list
        if (isSubject) {
            this.subjectQuillEditorRefs[template.id] = quillEditor;
        } else {
            this.quillEditorRefs[template.id] = quillEditor;
        }
    }

    onContentChanged(change: ContentChange, template: QuillTemplate, isSubject?: boolean): void {
        if (isSubject) {
            this.store.dispatch(TemplatesActions.templateContentEdited({
                templateId: template.id,
                subjectContents: change?.content,
            }));
        } else {
            this.store.dispatch(TemplatesActions.templateContentEdited({
                contents: change.content,
                templateId: template.id,
            }));
        }
    }

    saveTemplateChanges(template: QuillTemplate): void {
        this.store.dispatch(TemplatesActions.saveButtonClicked({
            template
        }))
    }

    discardTemplateChanges(template: QuillTemplate): void {
        this.quillEditorRefs[template.id].setContents([]);
        this.store.dispatch(TemplatesActions.discardButtonClicked({
            template
        }))
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    private asTemplateLocation(value: string): QuillTemplateLocation | undefined {
        const locations = Object.values(QuillTemplateLocation) as string[];

        return locations.includes(value) ? value as QuillTemplateLocation : undefined;
    }
}
